import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  getDashboard,
  listBlockTypes,
  runDashboard,
  saveBlocks,
  type BlockResult,
  type BlockTypeInfo,
  type Dashboard,
  type DashboardBlock as Block,
} from '../api/dashboards';
import { getProfile, getVersion, type ColumnSchema } from '../api/datasets';
import { BlockForm } from '../components/dashboards/BlockForm';
import { DashboardBlock } from '../components/dashboards/DashboardBlock';
import { AppShell } from '../components/layout/AppShell';
import { Alert, Button, Card, ErrorState, LoadingState } from '../components/ui';
import './DashboardEditPage.css';

type State = 'loading' | 'ready' | 'error';

const WIDTHS = [3, 4, 6, 8, 12];

/** `/dashboards/:id/edit` — the Coffee Lab. */
export function DashboardEditPage() {
  const { dashboardId = '' } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState<State>('loading');
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [types, setTypes] = useState<BlockTypeInfo[]>([]);
  const [schema, setSchema] = useState<ColumnSchema[]>([]);
  const [ranges, setRanges] = useState<
    Record<string, { minimum: number | null; maximum: number | null }>
  >({});
  const [results, setResults] = useState<Record<string, BlockResult>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    try {
      const loaded = await getDashboard(dashboardId);
      setDashboard(loaded);
      setBlocks(loaded.blocks);

      const [catalogue, version] = await Promise.all([
        listBlockTypes(),
        getVersion(loaded.dataset, loaded.dataset_version),
      ]);
      setTypes(catalogue);
      setSchema(version.column_schema);
      setState('ready');

      // The profile knows each number's range, which is what turns "about 20
      // buckets" into a bucket width the data actually has.
      try {
        const profile = await getProfile(loaded.dataset, loaded.dataset_version);
        setRanges(
          Object.fromEntries(
            profile.columns.map((column) => [
              column.name,
              { minimum: column.minimum, maximum: column.maximum },
            ]),
          ),
        );
      } catch {
        setRanges({});
      }

      const answer = await runDashboard(loaded.id);
      setResults(Object.fromEntries(answer.blocks.map((block) => [block.id, block])));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    }
  }, [dashboardId]);

  useEffect(() => {
    void load();
  }, [load]);

  const change = (next: Block[]) => {
    setBlocks(next);
    setDirty(true);
  };

  const move = (index: number, by: number) => {
    const target = index + by;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    const [moved] = next.splice(index, 1);
    if (moved) next.splice(target, 0, moved);
    change(next);
  };

  const resize = (index: number, key: 'w' | 'h', by: number) => {
    const block = blocks[index];
    if (!block) return;
    const next = [...blocks];
    const limits = key === 'w' ? WIDTHS : [2, 3, 4, 6, 8];
    const current = limits.indexOf(block.layout[key]);
    const position = Math.max(0, Math.min(limits.length - 1, (current === -1 ? 2 : current) + by));
    next[index] = { ...block, layout: { ...block.layout, [key]: limits[position] as number } };
    change(next);
  };

  const remove = (id: string) => change(blocks.filter((block) => block.id !== id));

  const upsert = (block: Block) => {
    const existing = blocks.findIndex((entry) => entry.id === block.id);
    if (existing === -1) change([...blocks, block]);
    else change(blocks.map((entry) => (entry.id === block.id ? block : entry)));
    setAdding(false);
    setEditing(null);
  };

  const save = async () => {
    if (!dashboard) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await saveBlocks(dashboard.id, blocks);
      setDashboard(saved);
      setBlocks(saved.blocks);
      setDirty(false);
      const answer = await runDashboard(saved.id);
      setResults(Object.fromEntries(answer.blocks.map((block) => [block.id, block])));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setSaving(false);
    }
  };

  const onDrop = (targetId: string) => {
    if (!dragging || dragging === targetId) return;
    const from = blocks.findIndex((block) => block.id === dragging);
    const to = blocks.findIndex((block) => block.id === targetId);
    if (from === -1 || to === -1) return;
    const next = [...blocks];
    const [moved] = next.splice(from, 1);
    if (moved) next.splice(to, 0, moved);
    change(next);
    setDragging(null);
  };

  if (state === 'loading') {
    return (
      <AppShell>
        <LoadingState label="Opening the lab…" />
      </AppShell>
    );
  }

  if (state === 'error' || !dashboard) {
    return (
      <AppShell>
        <ErrorState
          title="We could not open this dashboard"
          description={error?.message}
          requestId={error?.requestId}
          onRetry={() => void load()}
        />
      </AppShell>
    );
  }

  const editingBlock = blocks.find((block) => block.id === editing);

  // A filter drives blocks that ask a question; it cannot drive a note or
  // another filter.
  const targets = blocks
    .filter((block) => block.type !== 'filter' && block.type !== 'text')
    .map((block) => ({ id: block.id, title: block.title }));

  return (
    <AppShell
      header={
        <div className="sc-page-header">
          <div>
            <h1>{dashboard.title}</h1>
            <p>
              Coffee Lab · {blocks.length} block{blocks.length === 1 ? '' : 's'}
              {dirty ? ' · unsaved changes' : ''}
            </p>
          </div>
          <div className="sc-page-header__actions">
            <Link to={`/dashboards/${dashboard.id}`}>View dashboard</Link>
            <Button size="sm" isLoading={saving} disabled={!dirty} onClick={() => void save()}>
              Save
            </Button>
          </div>
        </div>
      }
    >
      {error && (
        <Alert tone="warning" title="That change could not be saved">
          {error.message}
        </Alert>
      )}

      <Card
        title={adding ? 'Add a block' : 'Blocks'}
        description={
          adding
            ? 'Choose what to show and what to show it of.'
            : 'Drag a block to reorder it, or use the buttons on each one.'
        }
        actions={
          !adding && (
            <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
              Add a block
            </Button>
          )
        }
      >
        {adding && (
          <BlockForm
            types={types}
            context={{ schema, ranges }}
            targets={targets}
            onSubmit={upsert}
            onCancel={() => setAdding(false)}
          />
        )}

        {!adding && blocks.length === 0 && (
          <p className="sc-lab__empty">
            Nothing here yet. Add a block, or start from a Quick Brew template on the dataset page.
          </p>
        )}
      </Card>

      {editingBlock && (
        <Card title={`Editing “${editingBlock.title}”`}>
          <BlockForm
            types={types}
            context={{ schema, ranges }}
            targets={targets}
            initial={{
              id: editingBlock.id,
              type: editingBlock.type,
              title: editingBlock.title,
              description: editingBlock.description,
              groupBy: [],
              measures: [],
              text: editingBlock.display.text ?? '',
              filterColumn: editingBlock.display.filter_field ?? '',
              filterOperator: editingBlock.display.filter_operator ?? 'in',
              targets: editingBlock.display.targets ?? [],
              layout: editingBlock.layout,
            }}
            onSubmit={upsert}
            onCancel={() => setEditing(null)}
          />
        </Card>
      )}

      <div className="sc-lab__grid">
        {blocks.map((block, index) => (
          <div
            key={block.id}
            className={dragging === block.id ? 'sc-lab__cell is-dragging' : 'sc-lab__cell'}
            style={{ gridColumn: `span ${Math.min(block.layout.w, 12)}` }}
            draggable
            onDragStart={() => setDragging(block.id)}
            onDragEnd={() => setDragging(null)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => onDrop(block.id)}
          >
            <div className="sc-lab__toolbar">
              <span className="sc-lab__grip" aria-hidden="true">
                ⠿
              </span>
              <span className="sc-lab__type">{block.type}</span>
              <span className="sc-lab__spacer" />

              {/*
                Everything a drag can do, a keyboard can do too: the arrows
                reorder and the plus and minus resize. The symbols are short so
                the toolbar does not shout over the block it belongs to; the
                accessible name spells each one out.
              */}
              <span
                className="sc-lab__controls"
                role="group"
                aria-label={`Layout of ${block.title}`}
              >
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Move earlier"
                  title="Move earlier"
                  onClick={() => move(index, -1)}
                >
                  ←
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Move later"
                  title="Move later"
                  onClick={() => move(index, 1)}
                >
                  →
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Narrower"
                  title="Narrower"
                  onClick={() => resize(index, 'w', -1)}
                >
                  ⇤
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Wider"
                  title="Wider"
                  onClick={() => resize(index, 'w', 1)}
                >
                  ⇥
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Shorter"
                  title="Shorter"
                  onClick={() => resize(index, 'h', -1)}
                >
                  ↥
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Taller"
                  title="Taller"
                  onClick={() => resize(index, 'h', 1)}
                >
                  ↧
                </Button>
              </span>

              <Button size="sm" variant="secondary" onClick={() => setEditing(block.id)}>
                Edit
              </Button>
              <Button size="sm" variant="danger" onClick={() => remove(block.id)}>
                Remove
              </Button>
            </div>
            <DashboardBlock block={block} state={results[block.id]} />
          </div>
        ))}
      </div>

      {blocks.length > 0 && (
        <p className="sc-lab__note">
          Changes are saved when you press Save — the backend checks every block against this
          dataset before it stores anything.
        </p>
      )}

      <div className="sc-lab__footer">
        <Button variant="secondary" onClick={() => navigate(`/dashboards/${dashboard.id}`)}>
          Done
        </Button>
      </div>
    </AppShell>
  );
}
